import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toastSuccess } from '../utils/toasts';
import {
  useCreateAudienceDiscountMutation,
  useUpdateAudienceDiscountMutation,
} from '../store';
import Switch from './atoms/Switch';
import Input from './atoms/Input';
import Button from './atoms/Button';
import useGetParams from '../hooks/useGetParams';
import { EventAudienceDiscountDTO } from '../models/event';

interface FormData {
  name: string;
  requirements: string;
  percentage: number;
  active: boolean;
}

interface CreateEditAudienceDiscountProps {
  discount?: EventAudienceDiscountDTO;
  onSuccess?: () => void;
}

const CreateEditAudienceDiscount: React.FC<CreateEditAudienceDiscountProps> = ({
  discount,
  onSuccess,
}) => {
  const [createAudienceDiscount, { isLoading: isCreatingDiscount }] = useCreateAudienceDiscountMutation();
  const [updateAudienceDiscount, { isLoading: isUpdatingDiscount }] = useUpdateAudienceDiscountMutation();
  const { businessId, eventId } = useGetParams();
  const {
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    register,
  } = useForm<FormData>({
    resolver: yupResolver(
      yup.object().shape({
        name: yup.string().required('El nombre es requerido'),
        requirements: yup.string().required('Los requisitos son requeridos'),
        percentage: yup
          .number()
          .required('El porcentaje es requerido')
          .typeError('El porcentaje debe ser un número'),
        active: yup.boolean().required('El estado es requerido'),
      }),
    ),
    defaultValues: {
      name: discount?.name || '',
      requirements: discount?.requirements || '',
      percentage: ((discount?.percentage || 0) * 100) || undefined,
      active: discount?.active || true,
    },
  });

  const onSubmit = async (data: FormData) => {
    const parsedData = {
      name: data.name,
      requirements: data.requirements,
      percentage: data.percentage / 100,
      active: data.active,
    };
    if (discount) {
      const res = await updateAudienceDiscount({
        businessId,
        eventId,
        discountId: discount.id,
        discountData: parsedData,
      });
      if (!('error' in res)) {
        toastSuccess('Descuento actualizado correctamente');
      }
    } else {
      const res = await createAudienceDiscount({
        businessId,
        eventId,
        discountData: parsedData,
      });
      if (!('error' in res)) {
        toastSuccess('Descuento creado correctamente');
      }
    }
    onSuccess?.();
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      <Switch
        id="discount-active"
        label="Descuento visible en la plataforma"
        checked={watch('active')}
        onChange={(e) => setValue('active', e.target.checked)}
      />
      <Input
        {...register('name')}
        id="discount-name"
        label="Nombre del descuento *"
        labelAbove
        errMsg={errors.name?.message}
      />
      <div>
        <Input
          {...register('requirements')}
          id="requirements"
          label="Requisitos *"
          labelAbove
          errMsg={errors.requirements?.message}
        />
        <p className="text-sm text-grey-500">
          Esta condición se verá en la pantalla de pago una vez que el cliente
          haga elegido comprar con este descuento.
        </p>
      </div>
      <Input
        {...register('percentage', { valueAsNumber: true })}
        type="number"
        id="discount-percentage"
        label="Porcentaje de descuento *"
        labelAbove
        errMsg={errors.percentage?.message}
      />
      <div className="flex flex-row gap-2">
        <Button
          variant="secondary"
          onClick={() => {
            onSuccess?.();
          }}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          loading={isCreatingDiscount || isUpdatingDiscount}
        >
          {discount ? 'Editar' : 'Agregar'}
        </Button>
      </div>
    </form>
  );
};

export default CreateEditAudienceDiscount;
